import { useState } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { Button } from '../../components/ui/Button';
import { Input } from '../../components/ui/Input';
import { loginWithWebAuthn, isWebAuthnSupported } from '../../lib/webauthn';

export function LoginPage() {
  const [username, setUsername] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const location = useLocation();

  const handleLogin = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsLoading(true);
    setError(null);

    try {
      if (!isWebAuthnSupported()) {
        throw new Error('WebAuthn is not supported in this browser');
      }

      await loginWithWebAuthn(username);

      // Redirect to previous location or home
      const from = (location.state as any)?.from?.pathname || '/';

      // Force a page reload to refresh AuthContext
      window.location.href = from;
    } catch (err: any) {
      setError(err.message || 'Login failed. Please try again.');
      console.error('Login error:', err);
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="bg-white dark:bg-slate-900 p-8 rounded-lg border border-slate-300 dark:border-slate-800">
      <h2 className="text-2xl font-bold text-slate-900 dark:text-slate-100 mb-6">Login</h2>

      {error && (
        <div className="mb-4 p-3 bg-red-100 dark:bg-red-900/50 border border-red-400 dark:border-red-700 rounded-lg text-red-800 dark:text-red-200 text-sm">
          {error}
        </div>
      )}

      <div className="mb-4 p-3 bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800 rounded-lg">
        <p className="text-sm text-blue-800 dark:text-blue-200">
          🔐 This site uses passwordless authentication with WebAuthn (fingerprint, Face ID, security key, etc.)
        </p>
      </div>

      <form onSubmit={handleLogin} className="space-y-4">
        <Input
          label="Username"
          type="text"
          value={username}
          onChange={(e) => setUsername(e.target.value)}
          placeholder="Enter your username"
          required
          autoComplete="username webauthn"
        />

        <Button type="submit" className="w-full" isLoading={isLoading}>
          Login with Biometrics
        </Button>
      </form>

      <p className="mt-4 text-center text-slate-500 dark:text-slate-400 text-sm">
        Don't have an account?{' '}
        <Link to="/register" className="text-brand-600 dark:text-brand-400 hover:text-brand-700 dark:hover:text-brand-500">
          Register
        </Link>
      </p>
    </div>
  );
}
